import { FC, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Add, OpenInNew } from '@mui/icons-material'
import { Box, Button, Card, Chip, Grid, TableContainer, TextField, Typography } from '@mui/material'

import GRCircularProgress from '../../../components/GRCircularProgress/GRCircularProgress'
import { GRTable, GRTableColumn } from '../../../components/GRTable/GRTable'
import { displaySnackBar } from '../../../features/snackbar'
import { useAppDispatch } from '../../../hooks/storeHooks'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import {
  useCreateImageDefinitionMutation,
  useCreateTextDefinitionMutation,
  useListImageDefinitionsQuery,
  useListTextDefinitionsQuery,
} from '../../api/ai-prompts'
import { CreateDefinitionDialog } from '../../features/ai-prompts/components/CreateDefinitionDialog/CreateDefinitionDialog'
import { AiPromptDefinitionResponseDTO, PromptType } from '../../features/ai-prompts/types/types'

type DefinitionRow = AiPromptDefinitionResponseDTO & { type: PromptType }

type CustomProps = { navigate: ReturnType<typeof useNavigate> }

const definitionColumns: GRTableColumn<DefinitionRow, CustomProps>[] = [
  {
    labelAccessor: () => <Trans i18nKey="internal-ai-prompts:col_name" />,
    accessor: ({ row }) => (
      <Typography variant="body2" fontWeight="bold">
        {row.name}
      </Typography>
    ),
    sortAccessor: ({ row }) => row.name,
  },
  {
    headerCellProps: { sx: { width: 100 } },
    labelAccessor: () => <Trans i18nKey="internal-ai-prompts:col_type" />,
    accessor: ({ row }) => <Chip label={row.type === 'text' ? 'Text' : 'Image'} size="small" variant="outlined" />,
    sortAccessor: ({ row }) => row.type,
  },
  {
    headerCellProps: { sx: { width: 120 } },
    labelAccessor: () => <Trans i18nKey="internal-ai-prompts:col_active_version" />,
    accessor: ({ row }) => <Typography variant="body2">v{row.activeVersion}</Typography>,
    sortAccessor: ({ row }) => row.activeVersion,
  },
  {
    headerCellProps: { sx: { width: 100 } },
    labelAccessor: () => <Trans i18nKey="internal-ai-prompts:col_actions" />,
    accessor: ({ row, customTableProps }) => (
      <Button size="small" startIcon={<OpenInNew />} onClick={() => customTableProps?.navigate(`/internal/ai-prompts/${row.type}/${row.name}`)}>
        <Trans i18nKey="internal-ai-prompts:view_details" />
      </Button>
    ),
  },
]

export const AiPromptsPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-ai-prompts'))
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [filterText, setFilterText] = useState('')
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [createTextDefinition, { isLoading: isCreatingText }] = useCreateTextDefinitionMutation()
  const [createImageDefinition, { isLoading: isCreatingImage }] = useCreateImageDefinitionMutation()

  const { data: textDefinitions, isLoading: isLoadingText, isError: isErrorText } = useListTextDefinitionsQuery()
  const { data: imageDefinitions, isLoading: isLoadingImage, isError: isErrorImage } = useListImageDefinitionsQuery()
  const isLoading = isLoadingText || isLoadingImage
  const isError = isErrorText || isErrorImage

  const allDefinitions: DefinitionRow[] = useMemo(() => {
    const textRows: DefinitionRow[] = (textDefinitions || []).map((d) => ({ ...d, type: 'text' as const }))
    const imageRows: DefinitionRow[] = (imageDefinitions || []).map((d) => ({ ...d, type: 'image' as const }))
    return [...textRows, ...imageRows].sort((a, b) => a.name.localeCompare(b.name))
  }, [textDefinitions, imageDefinitions])

  const filteredDefinitions = useMemo(() => {
    if (!filterText.trim()) return allDefinitions
    const needle = filterText.trim().toLowerCase()
    return allDefinitions.filter((d) => d.name.toLowerCase().includes(needle))
  }, [allDefinitions, filterText])

  const [columns, setColumns] = useState(definitionColumns)
  const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<DefinitionRow, CustomProps>[]) => {
    setColumns(updatedColumns)
  }, [])

  const customTableProps = useMemo(() => ({ navigate }), [navigate])

  const handleCreate = useCallback(
    async (values: { name: string; type: PromptType; fullText: string; months: number; reasoningLevel: 'low' | 'medium' | 'high' }) => {
      try {
        let result: AiPromptDefinitionResponseDTO
        if (values.type === 'text') {
          result = await createTextDefinition({
            name: values.name,
            fullText: values.fullText,
            months: values.months,
            reasoningLevel: values.reasoningLevel,
          }).unwrap()
        } else {
          result = await createImageDefinition({
            name: values.name,
            fullText: values.fullText,
          }).unwrap()
        }
        setCreateDialogOpen(false)
        dispatch(displaySnackBar({ message: t('internal-ai-prompts:definition_created'), severity: 'success', open: true }))
        navigate(`/internal/ai-prompts/${values.type}/${result.name}`)
      } catch {
        dispatch(displaySnackBar({ message: t('internal-ai-prompts:definition_create_error'), severity: 'error', open: true }))
      }
    },
    [createTextDefinition, createImageDefinition, dispatch, t, navigate]
  )

  if (isLoading) return <GRCircularProgress />

  if (isError) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, color: 'error.main' }}>
        <Typography variant="body1">{t('internal-ai-prompts:definitions_load_error')}</Typography>
      </Box>
    )
  }

  return (
    <>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        {t('internal-ai-prompts:page_title')}
      </Typography>

      <Card sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs>
            <TextField
              size="small"
              label={t('internal-ai-prompts:filter_prompt_label')}
              placeholder={t('internal-ai-prompts:filter_prompt_placeholder')}
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              fullWidth
              InputProps={{ sx: { fontFamily: 'monospace' } }}
            />
          </Grid>
          <Grid item>
            <Button variant="outlined" startIcon={<Add />} onClick={() => setCreateDialogOpen(true)}>
              {t('internal-ai-prompts:create_definition')}
            </Button>
          </Grid>
        </Grid>
      </Card>

      {allDefinitions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <Typography variant="body1">{t('internal-ai-prompts:no_definitions')}</Typography>
        </Box>
      ) : (
        <TableContainer component={Card}>
          <GRTable
            columns={columns}
            onColumnsUpdated={handleColumnsUpdate}
            rows={filteredDefinitions}
            customProps={customTableProps}
            rowIdKey={(row) => `${row.type}-${row.name}`}
            striped
            gridlines
            noRowsLabel={t('internal-common:no_rows')}
          />
        </TableContainer>
      )}

      <CreateDefinitionDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreate}
        isLoading={isCreatingText || isCreatingImage}
      />
    </>
  )
}
